import { Link } from "react-router-dom";
import { useUser } from "@clerk/clerk-react";
import { useSubscription } from "../hooks/useSubscription";

// ============================================
// ICONS
// ============================================

function IconCheck({ className = "w-5 h-5" }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M4.5 12.75l6 6 9-13.5"
      />
    </svg>
  );
}

function IconX({ className = "w-5 h-5" }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M6 18L18 6M6 6l12 12"
      />
    </svg>
  );
}

function IconShield({ className = "w-5 h-5" }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z"
      />
    </svg>
  );
}

function IconLoader({ className = "w-5 h-5" }: { className?: string }) {
  return (
    <svg
      className={`animate-spin ${className}`}
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}

// ============================================
// FEATURE ROW
// ============================================

function FeatureRow({
  label,
  free,
  essential,
  pro,
}: {
  label: string;
  free: string | boolean;
  essential: string | boolean;
  pro: string | boolean;
}) {
  const renderCell = (value: string | boolean) => {
    if (typeof value === "boolean") {
      return value ? (
        <IconCheck className="w-5 h-5 text-teal-500 mx-auto" />
      ) : (
        <IconX className="w-4 h-4 text-slate-300 mx-auto" />
      );
    }
    return <span className="text-sm text-slate-700 font-medium">{value}</span>;
  };

  return (
    <div className="grid grid-cols-4 gap-4 py-3 px-4 border-b border-slate-100 last:border-b-0 items-center">
      <div className="text-sm text-slate-600">{label}</div>
      <div className="text-center">{renderCell(free)}</div>
      <div className="text-center">{renderCell(essential)}</div>
      <div className="text-center">{renderCell(pro)}</div>
    </div>
  );
}

// ============================================
// MAIN COMPONENT
// ============================================

export default function Pricing() {
  const { isSignedIn } = useUser();
  const { tier, checkout, loading } = useSubscription();

  const handleSelectPlan = (plan: "essential" | "pro") => {
    if (!isSignedIn) {
      // Redirect to sign up — they'll come back to pricing after
      window.location.href = "/sign-up";
      return;
    }
    checkout(plan);
  };

  const isCurrentPlan = (plan: string) => tier === plan;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-teal-50/30">
      {/* Header */}
      <header className="bg-white/80 border-b border-slate-200 sticky top-0 z-10 backdrop-blur-sm">
        <div className="max-w-5xl mx-auto px-6 py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center shadow-sm shadow-teal-500/20">
              <svg
                className="w-[18px] h-[18px] text-white"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z"
                />
              </svg>
            </div>
            <h1 className="text-xl font-bold text-slate-900 tracking-tight">
              <span className="text-teal-500">Peer</span>Prep
            </h1>
          </Link>
          {isSignedIn ? (
            <Link
              to="/"
              className="text-sm font-medium text-slate-600 hover:text-slate-800 px-4 py-2 rounded-lg hover:bg-slate-100 transition-colors"
            >
              ← Back to cases
            </Link>
          ) : (
            <Link
              to="/sign-in"
              className="text-sm font-medium text-teal-600 hover:text-teal-700 px-4 py-2 rounded-lg hover:bg-teal-50 transition-colors"
            >
              Sign in
            </Link>
          )}
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-12">
        {/* Hero */}
        <div className="text-center mb-12">
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-3">
            Plans that fit your practice
          </h1>
          <p className="text-lg text-slate-500 max-w-2xl mx-auto">
            Start free with 2 PA letters per month. Upgrade when you're ready
            for unlimited generation, P2P call prep, and real-time call support.
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          {/* FREE */}
          <div className="bg-white rounded-2xl border border-slate-200 p-6 flex flex-col">
            <div className="mb-6">
              <p className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-1">
                Free
              </p>
              <div className="flex items-baseline gap-1">
                <span className="text-4xl font-bold text-slate-900">$0</span>
                <span className="text-slate-400 text-sm">/month</span>
              </div>
              <p className="text-sm text-slate-500 mt-2">
                Try PeerPrep with 2 free PA letters each month.
              </p>
            </div>

            <ul className="space-y-3 mb-8 flex-1">
              <li className="flex items-start gap-2.5">
                <IconCheck className="w-5 h-5 text-teal-500 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-slate-700">
                  2 prior auth letters / month
                </span>
              </li>
              <li className="flex items-start gap-2.5">
                <IconCheck className="w-5 h-5 text-teal-500 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-slate-700">
                  Insurer-specific intelligence
                </span>
              </li>
              <li className="flex items-start gap-2.5">
                <IconCheck className="w-5 h-5 text-teal-500 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-slate-700">
                  De-identified by design
                </span>
              </li>
            </ul>

            {isSignedIn && isCurrentPlan("free") ? (
              <div className="px-4 py-2.5 text-center text-sm font-medium text-slate-500 bg-slate-100 rounded-xl">
                Current plan
              </div>
            ) : !isSignedIn ? (
              <Link
                to="/sign-up"
                className="px-4 py-2.5 text-center text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-xl transition-colors"
              >
                Get started free
              </Link>
            ) : null}
          </div>

          {/* ESSENTIAL */}
          <div className="bg-white rounded-2xl border-2 border-teal-400 p-6 flex flex-col relative shadow-lg shadow-teal-100/50">
            {/* Popular badge */}
            <div className="absolute -top-3 left-1/2 -translate-x-1/2">
              <span className="px-3 py-1 bg-gradient-to-r from-teal-500 to-teal-600 text-white text-xs font-bold rounded-full shadow-md shadow-teal-500/30 uppercase tracking-wider">
                Most popular
              </span>
            </div>

            <div className="mb-6">
              <p className="text-sm font-semibold text-teal-600 uppercase tracking-wider mb-1">
                Essential
              </p>
              <div className="flex items-baseline gap-1">
                <span className="text-4xl font-bold text-slate-900">$29</span>
                <span className="text-slate-400 text-sm">/month</span>
              </div>
              <p className="text-sm text-slate-500 mt-2">
                Unlimited PA letters for every authorization battle.
              </p>
            </div>

            <ul className="space-y-3 mb-8 flex-1">
              <li className="flex items-start gap-2.5">
                <IconCheck className="w-5 h-5 text-teal-500 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-slate-700">
                  <span className="font-semibold">Unlimited</span> prior auth
                  letters
                </span>
              </li>
              <li className="flex items-start gap-2.5">
                <IconCheck className="w-5 h-5 text-teal-500 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-slate-700">
                  Insurer-specific intelligence
                </span>
              </li>
              <li className="flex items-start gap-2.5">
                <IconCheck className="w-5 h-5 text-teal-500 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-slate-700">
                  Guideline-backed citations
                </span>
              </li>
              <li className="flex items-start gap-2.5">
                <IconCheck className="w-5 h-5 text-teal-500 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-slate-700">
                  De-identified by design
                </span>
              </li>
            </ul>

            {isSignedIn && isCurrentPlan("essential") ? (
              <div className="px-4 py-2.5 text-center text-sm font-medium text-teal-700 bg-teal-50 border border-teal-200 rounded-xl">
                Current plan
              </div>
            ) : (
              <button
                onClick={() => handleSelectPlan("essential")}
                disabled={loading}
                className="px-4 py-2.5 text-center text-sm font-semibold text-white bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 rounded-xl shadow-md shadow-teal-500/25 hover:shadow-lg transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loading ? (
                  <IconLoader className="w-4 h-4" />
                ) : isSignedIn ? (
                  "Upgrade to Essential"
                ) : (
                  "Get started — $29/mo"
                )}
              </button>
            )}
          </div>

          {/* PRO */}
          <div className="bg-white rounded-2xl border border-slate-200 p-6 flex flex-col opacity-75">
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-1">
                <p className="text-sm font-semibold text-slate-500 uppercase tracking-wider">
                  Pro
                </p>
                <span className="px-2 py-0.5 text-xs font-semibold text-slate-500 bg-slate-100 rounded-full">
                  Coming Soon
                </span>
              </div>
              <div className="flex items-baseline gap-1">
                <span className="text-4xl font-bold text-slate-900">$49</span>
                <span className="text-slate-400 text-sm">/month</span>
              </div>
              <p className="text-sm text-slate-500 mt-2">
                Full suite: PA letters, P2P call prep, and live call support.
              </p>
            </div>

            <ul className="space-y-3 mb-8 flex-1">
              <li className="flex items-start gap-2.5">
                <IconCheck className="w-5 h-5 text-teal-500 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-slate-700">
                  Everything in Essential
                </span>
              </li>
              <li className="flex items-start gap-2.5">
                <IconCheck className="w-5 h-5 text-teal-500 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-slate-700">
                  <span className="font-semibold">P2P call prep</span> —
                  structured talking points
                </span>
              </li>
              <li className="flex items-start gap-2.5">
                <IconCheck className="w-5 h-5 text-teal-500 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-slate-700">
                  <span className="font-semibold">Call Shield</span> — never
                  miss a P2P call
                </span>
              </li>
              <li className="flex items-start gap-2.5">
                <IconCheck className="w-5 h-5 text-teal-500 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-slate-700">
                  <span className="font-semibold">Live rebuttal assist</span>{" "}
                  during calls
                </span>
              </li>
              <li className="flex items-start gap-2.5">
                <IconCheck className="w-5 h-5 text-teal-500 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-slate-700">
                  Real-time transcription
                </span>
              </li>
            </ul>

            <div className="px-4 py-2.5 text-center text-sm font-medium text-slate-400 bg-slate-100 rounded-xl">
              Coming soon
            </div>
          </div>
        </div>

        {/* Feature Comparison Table */}
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm mb-16">
          <div className="px-6 py-5 border-b border-slate-200 bg-slate-50/50">
            <h2 className="text-lg font-bold text-slate-900">Compare plans</h2>
          </div>

          {/* Column Headers */}
          <div className="grid grid-cols-4 gap-4 py-3 px-4 border-b border-slate-200 bg-slate-50/30">
            <div className="text-sm font-semibold text-slate-500">Feature</div>
            <div className="text-sm font-semibold text-slate-500 text-center">
              Free
            </div>
            <div className="text-sm font-semibold text-teal-600 text-center">
              Essential
            </div>
            <div className="text-sm font-semibold text-slate-400 text-center">
              Pro
              <span className="block text-xs font-normal">Coming soon</span>
            </div>
          </div>

          {/* Feature Rows */}
          <FeatureRow
            label="Prior auth letters"
            free="2 / month"
            essential="Unlimited"
            pro="Unlimited"
          />
          <FeatureRow
            label="Insurer intelligence"
            free={true}
            essential={true}
            pro={true}
          />
          <FeatureRow
            label="Guideline citations"
            free={true}
            essential={true}
            pro={true}
          />
          <FeatureRow
            label="PHI scrubbing"
            free={true}
            essential={true}
            pro={true}
          />
          <FeatureRow
            label="P2P call prep"
            free={false}
            essential={false}
            pro={true}
          />
          <FeatureRow
            label="Call Shield"
            free={false}
            essential={false}
            pro={true}
          />
          <FeatureRow
            label="Live rebuttal assist"
            free={false}
            essential={false}
            pro={true}
          />
          <FeatureRow
            label="Real-time transcription"
            free={false}
            essential={false}
            pro={true}
          />
          <FeatureRow
            label="Accountability-based rebuttals"
            free={false}
            essential={false}
            pro={true}
          />
        </div>

        {/* Trust / Security Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-50 border border-emerald-200/60 mb-4">
            <IconShield className="w-4 h-4 text-emerald-500" />
            <span className="text-sm font-medium text-emerald-700">
              De-identified by design
            </span>
          </div>
          <p className="text-sm text-slate-500 max-w-lg mx-auto">
            Patient identifiers are automatically removed before any data leaves
            your device. PeerPrep never stores, transmits, or processes PHI.
          </p>
        </div>

        {/* FAQ */}
        <div className="max-w-2xl mx-auto">
          <h2 className="text-xl font-bold text-slate-900 text-center mb-8">
            Common questions
          </h2>
          <div className="space-y-6">
            <div>
              <h3 className="text-sm font-semibold text-slate-800 mb-1">
                Can I cancel anytime?
              </h3>
              <p className="text-sm text-slate-500">
                Yes. Cancel through your account settings and your access
                continues until the end of your billing period. No refunds for
                partial months.
              </p>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-slate-800 mb-1">
                What counts as a "prior auth letter"?
              </h3>
              <p className="text-sm text-slate-500">
                Each time you generate a new PA letter from a case, that's one
                generation. Editing and regenerating an existing case does not
                count as a new generation.
              </p>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-slate-800 mb-1">
                What's Call Shield?
              </h3>
              <p className="text-sm text-slate-500">
                Call Shield answers peer-to-peer calls on your behalf, holds the
                reviewer on the line with a professional greeting, and alerts
                you instantly so you never miss a review call again.
              </p>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-slate-800 mb-1">
                Is my data safe?
              </h3>
              <p className="text-sm text-slate-500">
                PeerPrep is de-identified by design. Patient identifiers are
                scrubbed client-side before anything reaches our servers. We
                never store PHI.
              </p>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-slate-800 mb-1">
                Can I upgrade or downgrade later?
              </h3>
              <p className="text-sm text-slate-500">
                Yes. You can change your plan at any time. Changes take effect
                at the start of your next billing cycle.
              </p>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-200 mt-16 py-8">
        <div className="max-w-5xl mx-auto px-6 flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-sm text-slate-400">
            &copy; {new Date().getFullYear()} PeerPrep. All rights reserved.
          </p>
          <div className="flex gap-6">
            <Link
              to="/terms"
              className="text-sm text-slate-400 hover:text-slate-600 transition-colors"
            >
              Terms of Service
            </Link>
            <Link
              to="/privacy"
              className="text-sm text-slate-400 hover:text-slate-600 transition-colors"
            >
              Privacy Policy
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
